import { useRef, useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  FormControl,
  FormLabel,
  Button,
  Spinner,
  Badge,
  Tooltip,
} from '@chakra-ui/react'
import { Search, CheckCircle2, Mail, Ticket } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { TimerCell } from '@/components/helpdesk'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

/** Format date as "30 Apr 2025 - 09:30 AM" */
function formatSubmittedAt(value: string | null | undefined): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const day = d.getDate()
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[d.getMonth()]
  const year = d.getFullYear()
  const hours = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const h12 = hours % 12 || 12
  const minStr = minutes < 10 ? `0${minutes}` : String(minutes)
  return `${day} ${month} ${year} - ${h12}:${minStr} ${ampm}`
}

// Base column config; filter options are merged from API in component
const INCOMING_REQUEST_FIELDS: DataTableOptions['dataFields'] = {
  request_number: {
    name: 'Request ID',
    withSort: true,
    maxWidth: '110px',
  },
  client_id: {
    name: 'Client',
    withSort: true,
    withFilter: true,
    filterOptions: [],
    maxWidth: '160px',
  },
  service_type: {
    name: 'Request Type',
    withSort: true,
    withFilter: true,
    filterOptions: [],
    maxWidth: '130px',
  },
  request_source: {
    name: 'Source',
    withSort: true,
    maxWidth: '120px',
  },
  description: {
    name: 'Subject',
    withSort: false,
    maxWidth: '280px',
  },
  submitted_at: {
    name: 'Date and Time of Request',
    withSort: true,
    maxWidth: '200px',
  },
  timer: {
    name: 'Timer',
    withSort: false,
    maxWidth: '110px',
  },
}

type FilterOption = { value: string | number; label: string }

interface StatusBadgeProps {
  label: string
  count: number
  colorScheme: 'purple' | 'blue' | 'teal' | 'orange'
}

function StatusBadge({ label, count, colorScheme }: StatusBadgeProps) {
  const colorMap = {
    purple: { bg: 'purple.50', color: 'purple.700', border: 'purple.200' },
    blue: { bg: 'blue.50', color: 'blue.700', border: 'blue.200' },
    teal: { bg: 'teal.50', color: 'teal.700', border: 'teal.200' },
    orange: { bg: 'orange.50', color: 'orange.700', border: 'orange.200' },
  }
  const colors = colorMap[colorScheme]

  return (
    <Badge
      borderRadius="md"
      px={3}
      py={1.5}
      fontWeight="600"
      fontSize="sm"
      bg={colors.bg}
      color={colors.color}
      borderWidth="1px"
      borderColor={colors.border}
      variant="outline"
    >
      {label} : {count}
    </Badge>
  )
}

export default function IncomingRequestPage() {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [clientOptions, setClientOptions] = useState<FilterOption[]>([])
  const [requestTypeOptions, setRequestTypeOptions] = useState<FilterOption[]>([])
  const [acknowledgingId, setAcknowledgingId] = useState<number | null>(null)
  const [totalRequests, setTotalRequests] = useState(0)

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [clientsRes, typesRes] = await Promise.all([
          apiClient.get('/options/clients'),
          apiClient.get('/options/service-types/request-types'),
        ])
        const clients = Array.isArray(clientsRes.data) ? clientsRes.data : []
        const types = Array.isArray(typesRes.data) ? typesRes.data : []
        setClientOptions(
          clients.map((c: { id: number; name?: string; code?: string }) => ({
            value: c.id,
            label: c.name || c.code || String(c.id),
          }))
        )
        setRequestTypeOptions(
          types.map((t: { id: number; name?: string; code?: string }) => ({
            value: t.id,
            label: t.name || t.code || String(t.id),
          }))
        )
      } catch {
        setClientOptions([])
        setRequestTypeOptions([])
      }
    }
    loadOptions()
  }, [])

  const handleAcknowledge = useCallback(async (row: Record<string, unknown>) => {
    const id = row.id as number
    if (!id) return

    setAcknowledgingId(id)
    try {
      // Call the acknowledge endpoint
      await apiClient.post(`/support/my-request/${id}/acknowledge`)
      showSuccess('Success', 'Request acknowledged successfully')
      // Reload the table
      tableRef.current?.reload()
    } catch (error: any) {
      showError(error)
    } finally {
      setAcknowledgingId(null)
    }
  }, [showSuccess, showError])

  const navigate = useNavigate()

  const handleCreateTicket = useCallback(
    (row: Record<string, unknown>) => {
      navigate('/tickets/create', {
        state: { fromRequest: row },
      })
    },
    [navigate]
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      dataFields: {
        ...INCOMING_REQUEST_FIELDS,
        client_id: {
          ...INCOMING_REQUEST_FIELDS.client_id,
          filterOptions: clientOptions,
        },
        service_type: {
          ...INCOMING_REQUEST_FIELDS.service_type,
          filterOptions: requestTypeOptions,
        },
      },
      dataSource: '/support/my-request',
      primaryKey: 'id',
      softDelete: false,
      displayInModal: false,
      noRecordText: 'No Record Found',
      hideSelection: true,
      otherActions: [
        {
          name: 'Compose Email',
          onClick: (row: Record<string, unknown>) => {
            const email = row.contact_email as string | undefined
            if (email) {
              window.location.href = `mailto:${email}`
            }
          },
          show: () => true,
          render: (_row: Record<string, unknown>) => (
            <Tooltip label="Compose Email" placement="top" hasArrow>
              <IconButton
                aria-label="Compose Email"
                icon={<Mail size={18} />}
                variant="ghost"
                size="sm"
                colorScheme="gray"
                onClick={(e) => {
                  e.stopPropagation()
                  const email = _row.contact_email as string | undefined
                  if (email) {
                    window.location.href = `mailto:${email}`
                  }
                }}
              />
            </Tooltip>
          ),
        },
        {
          name: 'Acknowledge',
          onClick: handleAcknowledge,
          show: (row: Record<string, unknown>) => !row.acknowledged_at, // Only show if not already acknowledged
          render: (row: Record<string, unknown>, isLoading?: boolean, onClick?: () => void) => (
            <Button
              size="sm"
              colorScheme="blue"
              isLoading={isLoading}
              isDisabled={isLoading}
              onClick={(e) => {
                e.stopPropagation()
                onClick?.()
              }}
            >
              Acknowledge
            </Button>
          ),
        },
        {
          name: 'Create Ticket',
          onClick: handleCreateTicket,
          show: (row: Record<string, unknown>) => !!row.acknowledged_at, // Only show if already acknowledged
          render: (_row: Record<string, unknown>) => (
            <Tooltip label="Create Ticket" placement="top" hasArrow>
              <IconButton
                aria-label="Create Ticket"
                icon={<Ticket size={18} />}
                variant="ghost"
                size="sm"
                colorScheme="green"
                onClick={(e) => {
                  e.stopPropagation()
                  handleCreateTicket(_row)
                }}
              />
            </Tooltip>
          ),
        },
      ],
    }),
    [clientOptions, requestTypeOptions, handleAcknowledge, handleCreateTicket]
  )

  const permissions = useMemo(
    () => ({
      can_edit: false, // Disable default edit action
      can_delete: false, // Disable default delete action
      can_create: false,
      can_view: true,
      can_export: false,
      can_import: false,
    }),
    []
  )

  const tableParams = useMemo(
    () => ({
      search: searchQuery,
      incoming: 1,
      ...(dateFrom && { date_from: dateFrom }),
      ...(dateTo && { date_to: dateTo }),
    }),
    [searchQuery, dateFrom, dateTo]
  )

  const customRenderers = useMemo(
    () => ({
      client_id: (_value: unknown, row?: Record<string, unknown>) => {
        const name = (row?.client_name ?? row?.contact_name) as string | null | undefined
        return <Text as="span">{name ?? '–'}</Text>
      },
      request_source: (value: string | null | undefined) => {
        if (!value) return <Text as="span" color="gray.500">–</Text>
        // Format: capitalize first letter and replace underscores with spaces
        const formatted = value
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
        return <Text as="span">{formatted}</Text>
      },
      submitted_at: (value: string | null | undefined) => formatSubmittedAt(value),
      timer: (value: unknown, row?: Record<string, unknown>) => {
        const submittedAt = (row?.submitted_at ?? value) as string | null | undefined
        if (!submittedAt || typeof submittedAt !== 'string') return <Text as="span" color="gray.500">–</Text>
        return <TimerCell requestedAt={submittedAt} />
      },
    }),
    []
  )

  const handleSearch = () => {
    const query = searchRef.current?.value ?? ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleDataLoad = useCallback((_data: any[], meta?: { total?: number }) => {
    const total = meta?.total ?? 0
    setTotalRequests(total)
  }, [])

  return (
    <Box p={6} w="100%" minW={0} overflow="hidden" display="flex" flexDirection="column">
      <VStack spacing={2} align="stretch" flex={1} overflow="hidden" minH={0}>
        <Card flexShrink={0} display="flex" flexDirection="column" flex={1} minH={0} overflow="hidden">


          <CardBody
            p={4}
            flex="1"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            minH={0}
            position="relative"
          >
            <Box flexShrink={0} mb={4}>
              <HStack spacing={3} flexWrap="wrap">
                <StatusBadge label="Total Requests" count={totalRequests} colorScheme="purple" />
              </HStack>
            </Box>
            <Box borderBottom="2px" borderColor={colors.border} mb={4} />
            <Box flexShrink={0} mb={4}>
              <HStack justify="flex-start" align="flex-end" spacing={4} width="100%" flexWrap="wrap">
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by request #, contact, description..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
                <HStack align="flex-end" spacing={2}>
                  <FormControl width="auto">
                    <FormLabel fontSize="xs" mb={1} whiteSpace="nowrap">
                      From
                    </FormLabel>
                    <Input
                      type="date"
                      size="sm"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      max={dateTo || undefined}
                    />
                  </FormControl>
                  <FormControl width="auto">
                    <FormLabel fontSize="xs" mb={1} whiteSpace="nowrap">
                      To
                    </FormLabel>
                    <Input
                      type="date"
                      size="sm"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      min={dateFrom || undefined}
                    />
                  </FormControl>
                </HStack>
              </HStack>
            </Box>
            <Box flex="1" overflow="hidden" display="flex" flexDirection="column" minH={0}>
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                access={permissions}
                filterOptions={{
                  client_id: clientOptions,
                  service_type: requestTypeOptions,
                }}
                customRenderers={customRenderers}
                actionLoadingStates={{
                  ...(acknowledgingId !== null && { [acknowledgingId]: true }),
                }}
                onDataLoad={handleDataLoad}
              />
            </Box>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  )
}
